import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Flame } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Daily allowance popup for FREE renters, shown once per day when the swipe
// page loads (swipe_deck.jsx owns the once-per-day rule via localStorage).
// Purely informational: it tells them how many of their 20 free swipes are
// left and offers Renter Plus. Closing it costs nothing.
export default function SwipesLeftModal({ open, remaining, limit, onClose }) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  function goUnlimited() {
    onClose();
    navigate("/checkout/renter_plus_monthly", {
      state: {
        item: {
          name: "SwipRent Renter Plus monthly",
          price: 9.99,
          desc: "Unlimited swipes, unlimited rewinds and priority visibility.",
          recurring: "monthly",
        },
        checkout: { product: "renter_plus_monthly" },
      },
    });
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 380, damping: 32 }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Your free swipes today"
            className="relative w-full rounded-t-3xl bg-night-surface p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] shadow-ambient sm:max-w-md sm:rounded-3xl sm:pb-6"
          >
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="absolute right-3 top-3 rounded-full p-2 text-night-sub transition hover:bg-night hover:text-night-text"
            >
              <X size={18} />
            </button>

            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-night-tint text-brand-primary">
              <Flame size={22} strokeWidth={2} />
            </span>
            <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight text-night-text">
              You have {remaining} free swipe{remaining === 1 ? "" : "s"} today
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-night-sub">
              Free accounts get {limit || 20} swipes a day and they reset at midnight. Renter Plus
              gives you unlimited swiping, unlimited rewinds and priority visibility for $9.99 per month.
            </p>

            <button
              type="button"
              onClick={goUnlimited}
              className="mt-6 w-full rounded-full bg-brand-primary px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-primary-hover"
            >
              Go unlimited $9.99/mo
            </button>
            <button
              type="button"
              onClick={onClose}
              className="mt-2 w-full rounded-full px-4 py-3 text-sm font-semibold text-night-sub transition hover:bg-night hover:text-night-text"
            >
              Start swiping
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
